import { canCreate } from "@/lib/perms";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LayoutGrid, List, Plus, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useWorld } from "@/context/WorldContext";
import { splitOrigin } from "@/components/Origin";
import { CATEGORIES, SHIP_STATUSES, SPECIES_STATUSES, STATUS_ORDER } from "@/lib/constants";
import { SHIP_SORTERS, isSapient, shipLabel, speciesStatus } from "@/lib/infobox";
import { useShipSort } from "@/components/ShipBits";
import { EntryCard, EntryRow } from "@/components/CategoryEntries";
import { CollectionSection } from "@/components/CollectionSection";
import { NewCollectionButton } from "@/components/CollectionBits";
import { collectionsOf, groupByCollection } from "@/lib/collections";

const Entries = ({ items, view, meta, basePath, testId }) => (view === "grid" ? (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-4" data-testid={testId || "category-grid"}>
    {items.map((p, i) => <EntryCard key={p.id} page={p} meta={meta} i={i} to={`${basePath}/p/${p.id}`} />)}
  </div>
) : (
  <div className="rounded-xl border border-line bg-panel divide-y divide-line overflow-hidden" data-testid={testId || "category-list"}>
    {items.map((p) => <EntryRow key={p.id} page={p} meta={meta} to={`${basePath}/p/${p.id}`} />)}
  </div>
));

const ByOrigin = ({ shown, profile, ...rest }) => (
  <div className="space-y-4" data-testid="category-origin-groups">
    {splitOrigin(shown, profile).filter((g) => g.items.length).map((g) => (
      <CollectionSection key={g.key} id={`${rest.meta.key}-${g.key}`} name={g.name} count={g.items.length}>
        <Entries items={g.items} {...rest} testId={`origin-entries-${g.key}`} />
      </CollectionSection>
    ))}
  </div>
);

const Grouped = ({ shown, cols, readOnly, ...rest }) => {
  const { groups: all, rest: other } = groupByCollection(shown, cols);
  const groups = readOnly ? all.filter((g) => g.items.length) : all;
  return (
    <div className="space-y-4" data-testid="category-collections">
      {groups.map((g) => (
        <CollectionSection key={g.col.id} id={g.col.id} name={g.col.name} col={g.col} count={g.items.length}>
          <Entries items={g.items} {...rest} testId={`collection-entries-${g.col.id}`} />
        </CollectionSection>
      ))}
      {other.length > 0 && (
        <CollectionSection id={`${rest.meta.key}-other`} name="Other entries" count={other.length}>
          <Entries items={other} {...rest} testId="collection-entries-other" />
        </CollectionSection>
      )}
    </div>
  );
};

const VIEW_KEY = "lorely.categoryView";
const SINGULAR = { Characters: "character", Locations: "location", Factions: "faction", Lore: "lore entry", Events: "event", Ships: "ship", Species: "species" };
const SORTERS = {
  az: (a, b) => a.title.localeCompare(b.title),
  recent: (a, b) => b.updated_at.localeCompare(a.updated_at),
  status: SHIP_SORTERS.status,
  timeline: (a, b) => (a.event_order ?? Infinity) - (b.event_order ?? Infinity) || a.title.localeCompare(b.title),
};

const ViewToggle = ({ view, setView }) => (
  <div className="flex rounded-full border border-line p-0.5" data-testid="view-toggle">
    {[["grid", LayoutGrid], ["list", List]].map(([v, Icon]) => (
      <button key={v} onClick={() => setView(v)} data-testid={`view-${v}-btn`} aria-pressed={view === v}
        className={`p-1.5 px-3 rounded-full transition-colors ${view === v ? "bg-amber-500/15 text-amber-300" : "text-slate-500 hover:text-slate-200"}`}>
        <Icon className="w-4 h-4" />
      </button>
    ))}
  </div>
);

const CategoryBody = ({ meta }) => {
  const { pages, basePath, readOnly, collections, world, profile } = useWorld();
  const cols = collectionsOf(collections, meta.key);
  const isEvents = meta.key === "Events";
  const [q, setQ] = useState("");
  const isShips = meta.key === "Ships";
  const [ownSort, setOwnSort] = useState(isEvents ? "timeline" : "az");
  const [shipSort, setShipSort] = useShipSort();
  const [sort, setSort] = isShips ? [shipSort, setShipSort] : [ownSort, setOwnSort];
  const [sel, setSel] = useState([]);
  const [label, setLabel] = useState("");
  const [sapient, setSapient] = useState("");
  const [view, setViewState] = useState(() => localStorage.getItem(VIEW_KEY) || "grid");
  const setView = (v) => { localStorage.setItem(VIEW_KEY, v); setViewState(v); };

  const entries = useMemo(() => pages.filter((p) => p.category === meta.key), [pages, meta.key]);
  const allTags = useMemo(() => [...new Set(entries.flatMap((p) => p.tags || []))].sort(), [entries]);
  const shown = entries
    .filter((p) => !q || `${p.title} ${p.summary} ${(p.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()))
    .filter((p) => sel.every((t) => (p.tags || []).includes(t)))
    .filter((p) => !label || (meta.key === "Species" ? speciesStatus(p) : shipLabel(p)) === label)
    .filter((p) => !sapient || isSapient(p) === (sapient === "yes"))
    .sort(SORTERS[sort]);
  const clear = () => { setQ(""); setSel([]); setLabel(""); setSapient(""); };
  const toggleTag = (t) => setSel((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  const Icon = meta.icon;
  const newHref = `${basePath}/new?category=${meta.key}`;

  return (
    <div className="px-6 md:px-12 py-10 max-w-6xl 2xl:max-w-[1600px] space-y-6" data-testid={`category-page-${meta.key.toLowerCase()}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className={`flex items-center gap-2 font-code text-xs uppercase tracking-[0.3em] ${meta.text}`}><Icon className="w-4 h-4" /> Category</p>
          <h1 className="font-display text-4xl sm:text-5xl text-amber-50 mt-2">{meta.key} <span className="text-slate-600 text-3xl" data-testid="category-count">{entries.length}</span></h1>
        </div>
        {!readOnly && canCreate(world, meta.key) && (
          <div className="flex flex-wrap gap-2">
          {!profile && <NewCollectionButton category={meta.key} />}
          <Link to={newHref} data-testid="category-new-entry-btn" className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent text-sm font-semibold px-5 py-2.5 transition-colors">
            <Plus className="w-4 h-4" /> New {SINGULAR[meta.key]}
          </Link>
          </div>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line p-14 text-center" data-testid="category-empty">
          <p className="font-display text-3xl text-amber-100">No {meta.key.toLowerCase()} yet.</p>
          {readOnly ? <p className="text-slate-500 mt-2">This part of the world is still unwritten.</p> : (
            <Link to={newHref} className="inline-block mt-4 text-amber-400 hover:underline" data-testid="category-empty-create-link">Write the first {SINGULAR[meta.key]}</Link>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3" data-testid="category-toolbar">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${meta.key.toLowerCase()}`} className="pl-9 bg-canvas" data-testid="category-search-input" />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-44" data-testid="category-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-panel">
                {isEvents && <SelectItem value="timeline" data-testid="sort-option-timeline">Timeline order</SelectItem>}
                {isShips && <SelectItem value="status" data-testid="sort-option-status">By status</SelectItem>}
                <SelectItem value="az" data-testid="sort-option-az">A–Z</SelectItem>
                <SelectItem value="recent" data-testid="sort-option-recent">Recently updated</SelectItem>
              </SelectContent>
            </Select>
            <ViewToggle view={view} setView={setView} />
          </div>
          {isShips && (
            <div className="flex flex-wrap items-center gap-2" data-testid="ship-status-chips">
              {STATUS_ORDER.map((l) => (
                <button key={l} onClick={() => setLabel((c) => (c === l ? "" : l))} aria-pressed={label === l} data-testid={`ship-status-chip-${l.toLowerCase()}`}
                  className={`flex items-center gap-1.5 text-xs font-code uppercase tracking-wider rounded-full border px-3 py-1 transition-colors ${label === l ? SHIP_STATUSES[l].cls : "border-line text-slate-400 hover:text-slate-100"}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: SHIP_STATUSES[l].hex }} />{l}
                </button>
              ))}
            </div>
          )}
          {meta.key === "Species" && (
            <div className="flex flex-wrap items-center gap-2" data-testid="species-filters">
              {Object.keys(SPECIES_STATUSES).map((l) => (
                <button key={l} onClick={() => setLabel((c) => (c === l ? "" : l))} aria-pressed={label === l} data-testid={`species-status-chip-${l.toLowerCase()}`}
                  className={`flex items-center gap-1.5 text-xs font-code uppercase tracking-wider rounded-full border px-3 py-1 transition-colors ${label === l ? SPECIES_STATUSES[l].cls : "border-line text-slate-400 hover:text-slate-100"}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: SPECIES_STATUSES[l].hex }} />{l}
                </button>
              ))}
              <div className="flex rounded-full border border-line p-0.5 ml-1" data-testid="sapient-filter">
                {[["", "All"], ["yes", "Sapient"], ["no", "Non-sapient"]].map(([v, t]) => (
                  <button key={v || "all"} onClick={() => setSapient(v)} aria-pressed={sapient === v} data-testid={`sapient-filter-${v || "all"}`}
                    className={`text-xs font-code px-3 py-0.5 rounded-full transition-colors ${sapient === v ? "bg-sky-500/15 text-sky-200" : "text-slate-500 hover:text-slate-200"}`}>{t}</button>
                ))}
              </div>
            </div>
          )}
          {allTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="category-tag-chips">
              {allTags.map((t) => (
                <button key={t} onClick={() => toggleTag(t)} data-testid={`tag-chip-${t}`} aria-pressed={sel.includes(t)}
                  className={`text-xs font-code rounded-full border px-3 py-1 transition-colors ${sel.includes(t) ? `${meta.bg} ${meta.border} ${meta.text}` : "border-line text-slate-400 hover:text-slate-100"}`}>
                  #{t}
                </button>
              ))}
              {sel.length > 0 && <button onClick={() => setSel([])} className="text-xs text-slate-500 hover:text-amber-300 flex items-center gap-1" data-testid="clear-tags-btn"><X className="w-3 h-3" /> Clear</button>}
            </div>
          )}
          {shown.length === 0 ? (
            <div className="rounded-xl border border-line p-10 text-center" data-testid="category-no-matches">
              <p className="text-slate-400">Nothing matches your search or tags.</p>
              <button onClick={clear} className="mt-2 text-sm text-amber-400 hover:underline" data-testid="clear-filters-btn">Clear search and filters</button>
            </div>
          ) : profile ? <ByOrigin shown={shown} profile={profile} view={view} meta={meta} basePath={basePath} />
            : cols.length ? <Grouped shown={shown} cols={cols} readOnly={readOnly} view={view} meta={meta} basePath={basePath} />
            : <Entries items={shown} view={view} meta={meta} basePath={basePath} />}
        </>
      )}
    </div>
  );
};

export default function CategoryPage() {
  const { category } = useParams();
  const meta = CATEGORIES.find((c) => c.key.toLowerCase() === category);
  if (!meta) return <p className="p-12 text-slate-500" data-testid="category-not-found">No such category in this world.</p>;
  return <CategoryBody key={meta.key} meta={meta} />;
}
